import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSido,
  getSigungu,
  getDong,
  getStdPriceRows,
  upsertStdPrice,
} from '../../lib/api/admin-std-price-api';
import './StdPriceManagement.css';

const PRICE_FIELDS = [
  { key: 'prevHighPrice', label: '전고가' },
  { key: 'properHighPrice', label: '적고가' },
  { key: 'prevLowPrice', label: '전저가' },
  { key: 'maxPrice', label: '최고가' },
];

const rowKeyOf = (row) => `${row.naverDanjiCode || ''}__${row.ptpNo ?? ''}`;

// 콤마 제거 후 숫자, 빈 값은 null
const parsePrice = (text) => {
  const cleaned = String(text ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const formatPrice = (value) => (value === null || value === undefined || value === '' ? '' : Number(value).toLocaleString('ko-KR'));

function StdPriceManagement() {
  const { token } = useAuth();

  const [sidoList, setSidoList] = useState([]);
  const [sigunguList, setSigunguList] = useState([]);
  const [dongList, setDongList] = useState([]);
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [dong, setDong] = useState('');
  const [danjiName, setDanjiName] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // 시도 로드
  useEffect(() => {
    (async () => {
      try {
        setSidoList(await getSido(token));
      } catch (e) {
        setError(e?.message || '시도 조회에 실패했습니다.');
      }
    })();
  }, [token]);

  // 시도 변경 → 시군구 로드
  const handleSidoChange = useCallback(async (code) => {
    setSido(code);
    setSigungu('');
    setDong('');
    setSigunguList([]);
    setDongList([]);
    if (!code) return;
    try {
      setSigunguList(await getSigungu(token, code));
    } catch (e) {
      setError(e?.message || '시군구 조회에 실패했습니다.');
    }
  }, [token]);

  // 시군구 변경 → 읍면동 로드
  const handleSigunguChange = useCallback(async (code) => {
    setSigungu(code);
    setDong('');
    setDongList([]);
    if (!code) return;
    try {
      setDongList(await getDong(token, code));
    } catch (e) {
      setError(e?.message || '읍면동 조회에 실패했습니다.');
    }
  }, [token]);

  const selectedBjdCode = dong || sigungu || sido || '';

  const fetchPage = useCallback(async (targetPage) => {
    setLoading(true);
    setError('');
    try {
      const data = await getStdPriceRows(token, {
        bjdCode: selectedBjdCode,
        danjiName,
        page: targetPage,
        pageSize,
      });
      setRows(data.items);
      setTotal(data.total);
      setPage(data.page);
    } catch (e) {
      setError(e?.message || '기준가 조회에 실패했습니다.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, selectedBjdCode, danjiName, pageSize]);

  // 조회 버튼/엔터: 1페이지부터
  const loadRows = useCallback(() => fetchPage(1), [fetchPage]);

  // 가격 셀 저장 (행 전체 4개 가격값을 함께 upsert)
  const commitPrice = useCallback(async (rowIndex, field, newValue) => {
    const current = rows[rowIndex];
    if (!current) return;
    if ((current[field] ?? null) === newValue) return; // 변경 없음

    const updatedRow = { ...current, [field]: newValue };
    setRows((prev) => prev.map((r, i) => (i === rowIndex ? updatedRow : r)));

    const key = rowKeyOf(updatedRow);
    setSavingKey(key);
    const { ok, message } = await upsertStdPrice(token, {
      naverDanjiCode: updatedRow.naverDanjiCode,
      ptpNo: updatedRow.ptpNo,
      prevHighPrice: updatedRow.prevHighPrice ?? null,
      properHighPrice: updatedRow.properHighPrice ?? null,
      prevLowPrice: updatedRow.prevLowPrice ?? null,
      maxPrice: updatedRow.maxPrice ?? null,
    });
    setSavingKey('');
    if (!ok) {
      alert(message || '저장에 실패했습니다.');
      // 실패 시 원복
      setRows((prev) => prev.map((r, i) => (i === rowIndex ? current : r)));
    }
  }, [rows, token]);

  return (
    <div className="std-price-management">
      {!!error && <div className="admin-table-status error">{error}</div>}

      <div className="std-price-toolbar">
        <select value={sido} onChange={(e) => handleSidoChange(e.target.value)}>
          <option value="">시도 선택</option>
          {sidoList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <select value={sigungu} onChange={(e) => handleSigunguChange(e.target.value)} disabled={!sido}>
          <option value="">시군구 선택</option>
          {sigunguList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <select value={dong} onChange={(e) => setDong(e.target.value)} disabled={!sigungu}>
          <option value="">읍면동 선택</option>
          {dongList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <input
          type="text"
          className="std-price-search"
          value={danjiName}
          onChange={(e) => setDanjiName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') loadRows(); }}
          placeholder="단지명 검색"
        />
        <button type="button" className="std-price-btn primary" onClick={loadRows}>조회</button>
      </div>

      {loading && <div className="admin-table-status">불러오는 중입니다...</div>}

      <div className="std-price-table-wrap">
        <table className="admin-basic-table std-price-table">
          <thead>
            <tr>
              <th className="region-col">시도</th>
              <th className="region-col">시군구</th>
              <th className="region-col">읍면동</th>
              <th className="danji-col">단지명</th>
              <th className="code-col">단지코드</th>
              <th>평형명</th>
              {PRICE_FIELDS.map((f) => <th key={f.key} className="price-col">{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const key = rowKeyOf(row);
              const saving = savingKey === key;
              return (
                <tr key={key} className={saving ? 'saving' : ''}>
                  <td className="region-col">{row.sidoName || '-'}</td>
                  <td className="region-col">{row.sigunguName || '-'}</td>
                  <td className="region-col">{row.bjdName || '-'}</td>
                  <td className="danji-col">{row.buildingName || '-'}</td>
                  <td className="code-col muted">{row.naverDanjiCode || '-'}</td>
                  <td>{row.ptpName || '-'}</td>
                  {PRICE_FIELDS.map((f) => (
                    <td key={f.key} className="price-col">
                      <PriceCell value={row[f.key]} onCommit={(v) => commitPrice(idx, f.key, v)} />
                    </td>
                  ))}
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="muted center">조회된 기준가가 없습니다. 지역/단지명으로 조회하세요.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="std-price-pagination">
          <button type="button" className="std-price-btn" disabled={page <= 1 || loading} onClick={() => fetchPage(1)}>«</button>
          <button type="button" className="std-price-btn" disabled={page <= 1 || loading} onClick={() => fetchPage(page - 1)}>이전</button>
          <span className="std-price-page-info">
            {page} / {totalPages} 페이지 · 총 {total.toLocaleString('ko-KR')}건
          </span>
          <button type="button" className="std-price-btn" disabled={page >= totalPages || loading} onClick={() => fetchPage(page + 1)}>다음</button>
          <button type="button" className="std-price-btn" disabled={page >= totalPages || loading} onClick={() => fetchPage(totalPages)}>»</button>
        </div>
      )}
    </div>
  );
}

// 단일 클릭 인라인 편집 가격 셀: 포커스 시점 값과 비교해 변경 시에만 저장
function PriceCell({ value, onCommit }) {
  const [text, setText] = useState(formatPrice(value));
  const [initial, setInitial] = useState(formatPrice(value));

  useEffect(() => { setText(formatPrice(value)); }, [value]);

  const commit = () => {
    if (text === initial) return;
    onCommit(parsePrice(text));
    setInitial(text);
  };

  return (
    <input
      className="std-price-cell-input"
      value={text}
      onFocus={() => setInitial(text)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.target.blur(); }
        if (e.key === 'Escape') { setText(initial); e.target.blur(); }
      }}
      inputMode="numeric"
    />
  );
}

export default StdPriceManagement;
